import { normalizeLandingEventStatsResponse } from '@/api/landings';
import { api } from './http';
import type { IAdminBuild, IAdminProfit, IAdminUserDetail, IPartnerWorker } from '@/entities/admin-user.entity';
import type { IBuildLogLine } from '@/entities/build-live.entity';
import type { IClient } from '@/entities/client.entity';
import type { ILandingDomain, ILandingEventStats, ILandingLink, ILandingSite } from '@/entities/landing.entity';
import type { RunDetail, RunEvent, RunSummary, SessionSummary } from '@/entities/sender.entity';
import type { SenderAuthJobDetail, SenderProfileSettings } from '@/api/sender-panel';
import { ROUTES } from '@/app/routes';

function workerBase(workerProfileId: number) {
	return `/partner/workers/${workerProfileId}`;
}

export async function fetchPartnerWorkers() {
	const { data } = await api.get<{ workers: IPartnerWorker[] }>('/partner/workers');
	return data.workers;
}

export async function fetchPartnerWorkerDetail(workerProfileId: number) {
	const { data } = await api.get<{ user: IAdminUserDetail }>(workerBase(workerProfileId));
	return data.user;
}

export async function fetchPartnerWorkerClients(
	workerProfileId: number,
	params: { cursor?: string; limit?: number; isOnline?: boolean; search?: string },
) {
	const { data } = await api.get<{ clients: IClient[]; nextCursor: string | null }>(
		`${workerBase(workerProfileId)}/clients`,
		{ params },
	);
	return data;
}

export async function fetchPartnerWorkerBuilds(workerProfileId: number) {
	const { data } = await api.get<{ builds: IAdminBuild[] }>(`${workerBase(workerProfileId)}/builds`);
	return data.builds;
}

export async function fetchPartnerWorkerBuildLogs(workerProfileId: number, buildId: string) {
	const { data } = await api.get<{ buildId: string; lines: IBuildLogLine[]; failureMessage?: string }>(
		`${workerBase(workerProfileId)}/builds/${buildId}/logs`,
	);
	return data;
}

export async function fetchPartnerWorkerProfits(
	workerProfileId: number,
	params?: { cursor?: number; limit?: number },
) {
	const { data } = await api.get<{ profits: IAdminProfit[]; nextCursor: number | null }>(
		`${workerBase(workerProfileId)}/profits`,
		{ params },
	);
	return data;
}

export async function fetchPartnerWorkerLandingDomains(workerProfileId: number) {
	const { data } = await api.get<{ items: ILandingDomain[] }>(`${workerBase(workerProfileId)}/landings/domains`);
	return data.items ?? (data as { domains?: ILandingDomain[] }).domains ?? [];
}

export async function fetchPartnerWorkerLandingSites(workerProfileId: number) {
	const { data } = await api.get<{ items: ILandingSite[] }>(`${workerBase(workerProfileId)}/landings/sites`);
	return data.items ?? (data as { sites?: ILandingSite[] }).sites ?? [];
}

export async function fetchPartnerWorkerLandingLinks(
	workerProfileId: number,
	params?: { siteId?: number; cursor?: string; limit?: number },
) {
	const { data } = await api.get<{ items: ILandingLink[]; nextCursor?: string | null }>(
		`${workerBase(workerProfileId)}/landings/links`,
		{ params },
	);
	return data;
}

export async function fetchPartnerWorkerLandingEventStats(
	workerProfileId: number,
	params?: { period?: 'day' | 'week' | 'month' | 'all'; linkId?: string },
) {
	const { data } = await api.get<ILandingEventStats>(`${workerBase(workerProfileId)}/landings/events/stats`, {
		params,
	});
	return normalizeLandingEventStatsResponse(data);
}

export interface PartnerWorkerCustomTemplate {
	id: string;
	slug: string;
	title: string;
	entry: string;
	createdAt: string;
}

export async function fetchPartnerWorkerLandingTemplates(workerProfileId: number) {
	const { data } = await api.get<{ items: PartnerWorkerCustomTemplate[] }>(
		`${workerBase(workerProfileId)}/landings/custom-templates`,
	);
	return data.items;
}

export async function fetchPartnerWorkerSenderProfileSettings(workerProfileId: number) {
	const { data } = await api.get<SenderProfileSettings>(`${workerBase(workerProfileId)}/sender/profile-settings`);
	return data;
}

export async function fetchPartnerWorkerSenderAuthJobs(
	workerProfileId: number,
	params?: { status?: string; limit?: number },
) {
	const { data } = await api.get<{ items: SenderAuthJobDetail[] }>(
		`${workerBase(workerProfileId)}/sender/auth-jobs`,
		{ params },
	);
	return data.items;
}

export async function fetchPartnerWorkerSenderRuns(
	workerProfileId: number,
	params?: { status?: string; limit?: number; cursor?: string },
) {
	const { data } = await api.get<{ items: RunSummary[]; nextCursor?: string | null }>(
		`${workerBase(workerProfileId)}/sender/runs`,
		{ params },
	);
	return data;
}

export async function fetchPartnerWorkerSenderSessions(
	workerProfileId: number,
	params?: { status?: string; limit?: number },
) {
	const { data } = await api.get<{ items: SessionSummary[] }>(
		`${workerBase(workerProfileId)}/sender/sessions`,
		{ params },
	);
	return data.items;
}

export function buildPartnerWorkerSenderRunPath(workerProfileId: number, runId: string) {
	return `${ROUTES.worker}${workerProfileId}/sender/runs/${runId}`;
}

export async function fetchPartnerWorkerSenderRun(workerProfileId: number, runId: string) {
	const { data } = await api.get<RunDetail>(`${workerBase(workerProfileId)}/sender/runs/${runId}`);
	return data;
}

export async function fetchPartnerWorkerSenderRunEvents(
	workerProfileId: number,
	runId: string,
	params?: { limit?: number; cursor?: string },
) {
	const { data } = await api.get<{ items: RunEvent[]; nextCursor?: string | null }>(
		`${workerBase(workerProfileId)}/sender/runs/${runId}/events`,
		{ params },
	);
	return data;
}
